import { ROLE_INFO, stripOwnerOnlyActionKeys, TeamRole } from '@/@types/TeamMember';
import { Input } from '@/components/shared/Input/Input';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { TeamService } from '@/services/team.service';
import { useTeamActions } from '@/hooks/useTeamActions';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PermissionsAccordion } from './PermissionsAccordion';

interface InviteMemberSheetProps {
  open: boolean;
  onClose: () => void;
}

const ROLES: TeamRole[] = ['admin', 'viewer', 'financial', 'custom'];

export function InviteMemberSheet({ open, onClose }: InviteMemberSheetProps) {
  const { t } = useTranslation('team');
  const queryClient = useQueryClient();
  const { categories, rolePresets, getEmptyPermissions, isLoading: isLoadingActions } = useTeamActions();
  const [step, setStep] = useState(1);
  const totalSteps = 3;

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [selectedRole, setSelectedRole] = useState<TeamRole>('viewer');
  const [permissions, setPermissions] = useState<Record<string, string[]>>({});
  const [emailError, setEmailError] = useState('');

  const initializedRef = React.useRef(false);

  React.useEffect(() => {
    if (open && rolePresets?.viewer && !initializedRef.current) {
      initializedRef.current = true;
      setPermissions(stripOwnerOnlyActionKeys(rolePresets.viewer));
    }
    if (!open) {
      initializedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rolePresets]);

  const { mutateAsync: inviteMember, isPending } = useMutation({
    mutationFn: TeamService.inviteMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      toast({
        description: t('invite_success'),
      });
      handleClose();
    },
    onError: () => {
      toast({
        variant: 'destructive',
        description: t('invite_error'),
      });
    },
  });

  const handleClose = () => {
    initializedRef.current = false;
    setStep(1);
    setEmail('');
    setName('');
    setSelectedRole('viewer');
    setPermissions(stripOwnerOnlyActionKeys(rolePresets?.viewer ?? {}));
    setEmailError('');
    onClose();
  };

  const handleRoleSelect = (role: TeamRole) => {
    setSelectedRole(role);
    if (role === 'custom') {
      setPermissions(getEmptyPermissions());
    } else if (rolePresets?.[role]) {
      setPermissions(stripOwnerOnlyActionKeys(rolePresets[role]));
    }
  };

  const handlePermissionsChange = (newPermissions: Record<string, string[]>) => {
    if (selectedRole !== 'custom') {
      setSelectedRole('custom');
    }
    setPermissions(stripOwnerOnlyActionKeys(newPermissions));
  };

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) {
      setEmailError(t('email_required'));
      return false;
    }
    if (!emailRegex.test(value)) {
      setEmailError(t('email_invalid'));
      return false;
    }
    setEmailError('');
    return true;
  };

  const nextStep = () => {
    if (step === 1) {
      if (!validateEmail(email)) return;
    }
    setStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const prevStep = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    const hasAtLeastOnePermission = Object.values(permissions).some(
      (perms) => perms.length > 0
    );

    if (!hasAtLeastOnePermission) {
      toast({
        variant: 'destructive',
        description: t('permissions_required', 'Selecione pelo menos uma permissão.'),
      });
      return;
    }

    await inviteMember({
      email,
      name: name || undefined,
      permissions: stripOwnerOnlyActionKeys(permissions),
    });
  };

  const stepLabels = [
    t('step_member_info'),
    t('step_role'),
    t('step_permissions'),
  ];

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <Input
              label={t('email')}
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) validateEmail(e.target.value);
              }}
              placeholder="colaborador@email.com"
              helperText={emailError}
            />
            <Input
              label={t('name_optional')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('name_placeholder')}
            />
          </div>
        );

      case 2:
        return (
          <div className="space-y-3">
            {ROLES.map((role) => {
              const info = ROLE_INFO[role];
              const isSelected = selectedRole === role;
              return (
                <button
                  key={role}
                  type="button"
                  onClick={() => handleRoleSelect(role)}
                  className={cn(
                    'w-full p-4 rounded-lg border text-left transition-all duration-200',
                    isSelected
                      ? 'border-primary/50 bg-primary/5'
                      : 'border-colorBorder bg-card hover:border-primary/30 hover:bg-elevated/30'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{info.emoji}</span>
                      <div>
                        <h4 className="font-medium text-sm">{t(`roles.${role}`)}</h4>
                        <p className="text-xs text-muted mt-0.5">
                          {t(`roles.${role}_description`)}
                        </p>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-black" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        );

      case 3:
        return isLoadingActions ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="animate-spin h-6 w-6 text-muted" />
          </div>
        ) : (
          <PermissionsAccordion
            permissions={permissions}
            onChange={handlePermissionsChange}
            categories={categories}
          />
        );
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="md:w-[504px] w-full h-[100vh] max-h-[100vh] overflow-hidden p-0">
        <section className="h-full flex flex-col">
          <div className="relative flex-shrink-0">
            <div className="flex items-center justify-between px-6 my-6">
              <div className="text-lg font-medium text-zinc-100">
                {t('invite_member')}
              </div>
            </div>
            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#18181B]">
              <div
                className="h-full bg-primary transition-all duration-300 ease-out"
                style={{ width: `${(step / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          <div className="px-6 py-4 flex-shrink-0">
            <div className="flex items-center gap-1">
              {[1, 2, 3].map((s) => (
                <React.Fragment key={s}>
                  <div
                    className={cn(
                      'flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold transition-all duration-300 flex-shrink-0',
                      s < step
                        ? 'bg-primary text-black'
                        : s === step
                          ? 'bg-primary/20 border border-primary text-primary'
                          : 'bg-[#18181B] border border-[#27272a] text-zinc-600'
                    )}
                  >
                    {s < step ? <Check className="h-3 w-3" /> : s}
                  </div>
                  {s < 3 && (
                    <div
                      className={cn(
                        'flex-1 h-px transition-colors duration-300',
                        s < step ? 'bg-primary' : 'bg-[#27272a]'
                      )}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className="text-xs text-zinc-400 mt-2">{stepLabels[step - 1]}</p>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-2">
            {renderStep()}
          </div>

          <div className="flex items-center justify-between p-4 border-t border-colorBorder flex-shrink-0 max-sm:pb-12">
            <Button
              type="button"
              variant="ghost"
              onClick={step === 1 ? handleClose : prevStep}
              className="text-zinc-400 hover:text-zinc-100"
            >
              {step === 1 ? t('cancel') : t('back')}
            </Button>
            {step < totalSteps ? (
              <Button type="button" onClick={nextStep} className="px-6">
                {t('advance')}
              </Button>
            ) : (
              <Button
                disabled={isPending}
                type="button"
                onClick={handleSubmit}
                className="px-6"
              >
                {isPending ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="animate-spin h-4 w-4" />
                    {t('sending')}
                  </span>
                ) : (
                  t('send_invite')
                )}
              </Button>
            )}
          </div>
        </section>
      </SheetContent>
    </Sheet>
  );
}
